// Auto-detects the visitor's browser language and offers a one-click switch.
// Discreet banner — appears once per visitor, dismissable, never shows for the FR default.
import { useEffect, useState } from "react";
import { useI18n } from "@/i18n/I18nContext";
import FR from "country-flag-icons/react/3x2/FR";
import GB from "country-flag-icons/react/3x2/GB";
import IT from "country-flag-icons/react/3x2/IT";
import ES from "country-flag-icons/react/3x2/ES";
import DE from "country-flag-icons/react/3x2/DE";
import PT from "country-flag-icons/react/3x2/PT";
import CN from "country-flag-icons/react/3x2/CN";
import PL from "country-flag-icons/react/3x2/PL";
import RU from "country-flag-icons/react/3x2/RU";
import SE from "country-flag-icons/react/3x2/SE";
import NO from "country-flag-icons/react/3x2/NO";

const STORAGE_KEY = "prieure_lang_suggest_ack";

const LANG_META = {
  fr: { label: "Français",  Flag: FR, prompt: "Cette page est disponible en français.",    accept: "Afficher en français" },
  en: { label: "English",   Flag: GB, prompt: "This page is available in English.",         accept: "Switch to English" },
  it: { label: "Italiano",  Flag: IT, prompt: "Questa pagina è disponibile in italiano.",   accept: "Passa all'italiano" },
  es: { label: "Español",   Flag: ES, prompt: "Esta página está disponible en español.",    accept: "Cambiar a español" },
  de: { label: "Deutsch",   Flag: DE, prompt: "Diese Seite ist auf Deutsch verfügbar.",     accept: "Auf Deutsch anzeigen" },
  pt: { label: "Português", Flag: PT, prompt: "Esta página está disponível em português.",  accept: "Mudar para português" },
  zh: { label: "中文",       Flag: CN, prompt: "此页面提供中文版本。",                          accept: "切换到中文" },
  pl: { label: "Polski",    Flag: PL, prompt: "Ta strona jest dostępna w języku polskim.",  accept: "Przełącz na polski" },
  ru: { label: "Русский",   Flag: RU, prompt: "Эта страница доступна на русском языке.",   accept: "Переключиться на русский" },
  sv: { label: "Svenska",   Flag: SE, prompt: "Denna sida är tillgänglig på svenska.",      accept: "Byt till svenska" },
  nb: { label: "Norsk",     Flag: NO, prompt: "Denne siden er tilgjengelig på norsk.",     accept: "Bytt til norsk" },
};

const DISMISS_LABEL = {
  fr: "Non merci", en: "No thanks", it: "No grazie", es: "No, gracias", de: "Nein danke",
  pt: "Não, obrigado", zh: "不用了,谢谢", pl: "Nie, dziękuję", ru: "Нет, спасибо",
  sv: "Nej tack", nb: "Nei takk",
};

export default function LanguageDetectionBanner() {
  const { locale, setLocale } = useI18n();
  const [suggested, setSuggested] = useState(null);

  useEffect(() => {
    if (typeof window === "undefined" || typeof navigator === "undefined") return;
    // Don't show if user already dismissed once or accepted the suggestion
    if (window.localStorage.getItem(STORAGE_KEY)) return;

    const detected = (navigator.language || "").slice(0, 2).toLowerCase();
    if (!LANG_META[detected]) return;
    if (detected === locale) return;

    const timer = setTimeout(() => setSuggested(detected), 1200);
    return () => clearTimeout(timer);
  }, [locale]);

  const acknowledge = () => {
    try { window.localStorage.setItem(STORAGE_KEY, new Date().toISOString()); } catch { /* ignore */ }
    setSuggested(null);
  };

  const accept = () => {
    if (suggested) setLocale(suggested);
    acknowledge();
  };

  if (!suggested) return null;

  const meta = LANG_META[suggested];
  const SuggestedFlag = meta.Flag;
  const dismissText = DISMISS_LABEL[suggested] || DISMISS_LABEL.en;

  return (
    <div
      data-testid="lang-detect-banner"
      role="dialog"
      aria-label={meta.label}
      style={{
        position: "fixed",
        top: "1rem",
        right: "1rem",
        maxWidth: "min(360px, calc(100vw - 2rem))",
        zIndex: 75,
        background: "rgba(245,240,228,0.98)",
        backdropFilter: "blur(14px) saturate(180%)",
        WebkitBackdropFilter: "blur(14px) saturate(180%)",
        border: "1px solid var(--border)",
        borderTopWidth: "3px",
        borderTopColor: "var(--gold)",
        boxShadow: "0 18px 50px rgba(20,16,12,0.30)",
        padding: "0.9rem 1rem 0.95rem",
        color: "var(--ink)",
        fontSize: "0.82rem",
        lineHeight: 1.45,
      }}
    >
      <div className="flex items-start gap-3" style={{ marginBottom: "0.65rem" }}>
        <SuggestedFlag
          data-testid={`lang-detect-flag-${suggested}`}
          title={meta.label}
          style={{ width: 28, height: 20, borderRadius: 2, boxShadow: "0 0 0 1px rgba(20,16,12,0.12)", flexShrink: 0, marginTop: 2 }}
        />
        <div style={{ flex: 1 }}>
          <span className="font-caps text-[0.55rem] block mb-1"
                style={{ letterSpacing: "0.22em", color: "var(--gold)" }}>
            ◇ {meta.label.toUpperCase()} ◇
          </span>
          <div>{meta.prompt}</div>
        </div>
        <button
          type="button"
          data-testid="lang-detect-close"
          onClick={acknowledge}
          aria-label={dismissText}
          style={{
            background: "transparent", border: "none", color: "var(--muted)",
            cursor: "pointer", padding: "2px 4px", lineHeight: 1, fontSize: "1rem",
          }}
        >
          ×
        </button>
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={accept}
          data-testid="lang-detect-accept"
          className="btn btn-primary"
          style={{ fontSize: "0.7rem", padding: "0.5rem 0.9rem", letterSpacing: "0.08em", flex: 1 }}
        >
          {meta.accept}
        </button>
        <button
          type="button"
          onClick={acknowledge}
          data-testid="lang-detect-dismiss"
          className="btn btn-ghost"
          style={{ fontSize: "0.7rem", padding: "0.5rem 0.9rem", letterSpacing: "0.08em" }}
        >
          {dismissText}
        </button>
      </div>
    </div>
  );
}
